/**
 * Returns APCA-based contrast between two colors.
 * Order of colors does not matter.
 * @param colorA Color in FULL HEX format.
 * @param colorB Color in FULL HEX format.
 */
export const getContrast = (colorA: string, colorB: string) => {
    const normalizedA = hexToNormalizedRGB(colorA);
    const normalizedB = hexToNormalizedRGB(colorB);

    const relativeBrightnessA = relativeBrightness(normalizedA);
    const relativeBrightnessB = relativeBrightness(normalizedB);

    const light = Math.max(relativeBrightnessA, relativeBrightnessB) ** 0.57;
    const dark = Math.min(relativeBrightnessA, relativeBrightnessB) ** 0.56;

    const contrast = (light - dark) * 100;
    return Math.abs(contrast);
};

const hexToNormalizedRGB = (hex: string): [number, number, number] => {
    let clean = hex;
    if (clean.startsWith("#")) clean = clean.slice(1)
    if (clean.length === 3) clean = clean.split('').map(sym => sym+=sym).join('');

    const r = parseInt(clean.slice(0, 2), 16) / 255;
    const g = parseInt(clean.slice(2, 4), 16) / 255;
    const b = parseInt(clean.slice(4, 6), 16) / 255;

    return [r, g, b];
};

const linearRGB = (color: number) =>
    color <= 0.04045 ? color / 12.92 : ((color + 0.055) / 1.055) ** 2.4;

const relativeBrightness = ([r, g, b]: [number, number, number]) =>
    linearRGB(r) * 0.2126 + linearRGB(g) * 0.7152 + linearRGB(b) * 0.0722;

/**
 * Returns true if contrast between two colors visible enough.
 * Order of colors does not matter.
 *
 * @param colorA Color in FULL HEX format.
 * @param colorB Color in FULL HEX format.
 * @param threshold Optional threshold (DEFAULT 30).
 *
 * 0–15	    Bad
 *
 * 15–30	Only for big headers
 *
 * 30–45	Okay for UI
 *
 * 45–60	Okay for regular text
 *
 * 60–75	Okay for small text
 *
 * 75–100	Maximum contrast
 *
 */
export const checkContrast = (
    colorA: string,
    colorB: string,
    threshold = 30
) => {
    const contrast = getContrast(colorA, colorB);
    return contrast >= threshold;
};
